import { useEffect, useState } from "react";

import { CommandGetOpsMargin } from "../commands/cmdGetOpsMargin";
import type { MarginSummary, MarginTrendPoint } from "../opsDashboard.types";
import styles from "../opsDashboard.module.css";

type View = "weekly" | "monthly";

/**
 * Margin Tracking — the three stat tiles, the trend chart and the definition copy
 * (MPN-1861). The Margin By Campaign table underneath is an ordinary tile and is
 * rendered by the section, not here.
 *
 * The definition sits on the page rather than in a tooltip because the design puts it
 * there: the number is unintuitive enough that it has to be read before the figures
 * above it mean anything.
 */
export function MarginTracking() {
    const [data, setData] = useState<MarginSummary | null>(null);
    const [view, setView] = useState<View>("weekly");

    useEffect(() => {
        let cancelled = false;
        new CommandGetOpsMargin()
            .run()
            .then((got) => {
                if (!cancelled) setData(got);
            })
            .catch(() => {
                // The table below still renders; a missing headline should not take the
                // section with it.
                if (!cancelled) setData(null);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (!data) return null;

    const target = data.target_pct;
    const bars: MarginTrendPoint[] = view === "weekly" ? data.weekly : data.monthly;

    return (
        <>
            <div className={styles.marginStats}>
                <Stat
                    value={data.blended_pct == null ? "—" : `${data.blended_pct.toFixed(1)}%`}
                    label="Blended Portfolio Margin"
                />
                <Stat value={String(data.at_or_above)} label="Campaigns At/Above Target Margin" tone="good" />
                <Stat value={String(data.below)} label="Campaigns Below Target Margin" tone="bad" />
            </div>

            <p className={styles.marginDef}>
                <strong>Margin definition:</strong> (Budget/revenue available for the period
                &minus; spend for the period) &divide; revenue for the period. This is a
                target-monitoring metric &mdash; how margin compares to our target ({target}%).
                A client&rsquo;s margin should never actually go negative; a negative reading
                indicates a real problem to investigate immediately, not an expected state.
            </p>

            <div className={styles.marginCard}>
                <div className={styles.marginCardHead}>
                    <h3 className={styles.marginCardTitle}>Margin Trend</h3>
                    <span className={styles.marginCardNote}>
                        blended across live campaigns · weekly / monthly only
                    </span>
                </div>
                <div className={styles.marginChart}>
                    <div className={styles.marginToggle} role="group" aria-label="Trend period">
                        {(["weekly", "monthly"] as View[]).map((v) => (
                            <button
                                key={v}
                                type="button"
                                className={`${styles.marginToggleBtn} ${
                                    view === v ? styles.marginToggleOn : ""
                                }`}
                                aria-pressed={view === v}
                                onClick={() => setView(v)}
                            >
                                {v === "weekly" ? "Weekly" : "Monthly"}
                            </button>
                        ))}
                    </div>
                    {bars.length === 0 ? (
                        <p className={styles.marginEmpty}>
                            No closed {view === "weekly" ? "weeks" : "months"} with a margin to
                            show yet.
                        </p>
                    ) : (
                        <>
                            <div className={styles.marginBars}>
                                {bars.map((b) => (
                                    <div key={b.label} className={styles.marginBarCol}>
                                        <div
                                            className={`${styles.marginBarVal} ${
                                                b.pct >= target ? styles.marginOver : styles.marginUnder
                                            }`}
                                        >
                                            {b.pct.toFixed(1)}%
                                        </div>
                                        {/* Height is a share of 100%, floored so a very low
                                            bar is still visibly a bar. A negative margin
                                            draws at the floor rather than mirrored: an
                                            absolute height would make −20% as tall as
                                            +20%, which is the opposite of what happened. */}
                                        <div
                                            className={`${styles.marginBar} ${
                                                b.pct >= target ? styles.marginBarOver : styles.marginBarUnder
                                            }`}
                                            style={{ height: Math.max(6, (Math.max(0, b.pct) / 100) * 150) }}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className={styles.marginBarLabels}>
                                {bars.map((b) => (
                                    <div key={b.label} className={styles.marginBarLabel}>
                                        {b.label}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

function Stat({ value, label, tone }: { value: string; label: string; tone?: "good" | "bad" }) {
    const toneClass = tone === "good" ? styles.marginStatGood : tone === "bad" ? styles.marginStatBad : "";
    return (
        <div className={styles.marginStat}>
            <div className={`${styles.marginStatValue} ${toneClass}`}>{value}</div>
            <div className={styles.marginStatLabel}>{label}</div>
        </div>
    );
}
